import { applyBps, assertMicros, splitCharge } from "@dbindex/contracts";
import { costExactMicros, priceRow, type InfraPriceTable } from "./table";

export interface HostingInputs {
  rowCount: number;
  avgRowBytes: number;
  changesPerSecond: number;
  freshnessSeconds: number | null;
  queriesPerMinute: number;
  cacheHitRatio: number;
  shards: number;
  semanticIndex: boolean;
}

export interface CostLine { product: string; quantity: number; unit: string; costMicros: number; note: string }
export interface CostEstimate {
  lowMicros: number;
  highMicros: number;
  expectedMicros: number;
  breakdown: CostLine[];
  assumptions: string[];
  tableVersion: string;
}

/** Workload model assumptions (not prices). Kept here so they surface in every estimate. */
export const WORKLOAD_ASSUMPTIONS = {
  secondsPerMonth: 30 * 86_400,
  minutesPerMonth: 30 * 1_440,
  manualFreshnessSeconds: 86_400,
  indexWriteAmplification: 3, // row + ~2 secondary index entries per change
  storageOverhead: 2, // indexes + SQLite page overhead
  heartbeatSeconds: 30,
  cpuMsHit: 2,
  cpuMsMiss: 6,
  rowsReadPerMiss: 50,
  doSecondsPerRequest: 0.02,
  doMemoryGb: 0.128,
  ingestDoSecondsPerBatch: 0.05,
  usageEventsPerQueueMessage: 10, // edge batches usage events per isolate
  queueOpsPerMessage: 3, // write + read + delete
  kvReadsPerQuery: 0.05, // API-key record cached in isolate memory
  missFanout: 1.5, // average shards touched per uncached query
  discoveryDimensions: 768,
  discoveryDocsPerDataset: 6,
  lowFactor: 0.7,
  highFactor: 1.6,
} as const;

const A = WORKLOAD_ASSUMPTIONS;

function line(table: InfraPriceTable, product: string, quantity: number, note: string): CostLine & { exact: number } {
  const exact = costExactMicros(table, product, quantity);
  return { product, quantity: Math.round(quantity * 1000) / 1000, unit: priceRow(table, product).unit, costMicros: Math.round(exact), note, exact };
}

/**
 * Estimate the monthly marginal infrastructure cost of hosting one dataset.
 * Account-wide free allowances are NOT subtracted (they are shared across all tenants),
 * which makes per-dataset figures deliberately conservative.
 */
export function estimateMonthlyHostingCost(i: HostingInputs, table: InfraPriceTable): CostEstimate {
  const rows = Math.max(0, i.rowCount);
  const shards = Math.max(1, Math.floor(i.shards));
  const hit = Math.min(1, Math.max(0, i.cacheHitRatio));
  const window = i.freshnessSeconds && i.freshnessSeconds > 0 ? i.freshnessSeconds : A.manualFreshnessSeconds;
  const windows = A.secondsPerMonth / window;

  // Ingestion: changes to the same row coalesce within a freshness window. Distinct rows touched per
  // window under uniform updates = rows·(1 − e^(−changesPerWindow/rows)).
  const rawChanges = Math.max(0, i.changesPerSecond) * A.secondsPerMonth;
  const perWindow = Math.max(0, i.changesPerSecond) * window;
  const changes = rows > 0 ? Math.min(rawChanges, rows * (1 - Math.exp(-perWindow / rows)) * windows) : 0;
  const batches = rawChanges > 0 ? windows * shards : 0; // one batch per shard per window while the source changes
  const heartbeats = A.secondsPerMonth / A.heartbeatSeconds;

  // Queries.
  const queries = Math.max(0, i.queriesPerMinute) * A.minutesPerMonth;
  const misses = queries * (1 - hit);
  const storageGb = (rows * Math.max(0, i.avgRowBytes) * A.storageOverhead) / 1e9;

  const lines = [
    line(table, "workers.requests", queries + batches / shards + heartbeats, "query + ingest + heartbeat requests"),
    line(table, "workers.cpu_ms", queries * hit * A.cpuMsHit + misses * A.cpuMsMiss + batches * 5, "edge CPU (hits are cheaper than misses)"),
    line(table, "do.requests", misses * A.missFanout + batches, "serving shard calls on cache miss + ingest batches"),
    line(table, "do.duration", (misses * A.missFanout * A.doSecondsPerRequest + batches * A.ingestDoSecondsPerBatch) * A.doMemoryGb, "shard wall-time"),
    line(table, "do.sqlite.rows_read", misses * A.rowsReadPerMiss, "rows scanned per uncached query"),
    line(table, "do.sqlite.rows_written", changes * A.indexWriteAmplification + rows * A.indexWriteAmplification / 12, "CDC upserts (+ amortised monthly re-snapshot)"),
    line(table, "do.sqlite.storage", storageGb, "serving projection + indexes"),
    line(table, "kv.reads", queries * A.kvReadsPerQuery, "edge config reads (mostly isolate-cached)"),
    line(table, "kv.writes", Math.min(windows, A.secondsPerMonth / 10), "dataset status/freshness publication"),
    line(table, "queues.operations", (queries / A.usageEventsPerQueueMessage) * A.queueOpsPerMessage, "metering → settlement"),
    line(table, "analytics_engine.data_points", queries, "operational query metrics"),
    ...(i.semanticIndex
      ? [
          line(table, "vectorize.stored_dimensions", A.discoveryDimensions * A.discoveryDocsPerDataset, "discovery documents (one per capability, not per row)"),
          line(table, "workers_ai.neurons", A.discoveryDocsPerDataset * 50, "re-embedding discovery documents"),
        ]
      : []),
    line(table, "dbindex.platform_dataset", 1, "DBIndex platform fee per active dataset"),
  ];
  const expected = lines.reduce((s, l) => s + l.costMicros, 0);
  const breakdown: CostLine[] = lines.map(({ exact: _e, ...l }) => l);
  return {
    expectedMicros: assertMicros(expected),
    lowMicros: assertMicros(Math.round(expected * A.lowFactor)),
    highMicros: assertMicros(Math.round(expected * A.highFactor)),
    breakdown,
    assumptions: [
      `Freshness window ${window}s (${i.freshnessSeconds ? "configured" : "manual → daily"}); changes coalesce per window.`,
      `${Math.round(hit * 100)}% cache hit ratio; uncached queries touch ~${A.missFanout} shards and read ~${A.rowsReadPerMiss} rows.`,
      `Storage overhead ×${A.storageOverhead} for indexes; ${A.indexWriteAmplification} row writes per change.`,
      `Usage events batched ${A.usageEventsPerQueueMessage}/queue message.`,
      "Account-wide free allowances are not subtracted (conservative per-dataset marginal cost).",
      `Range ${A.lowFactor}× – ${A.highFactor}× of expected. Prices from table ${table.version}.`,
    ],
    tableVersion: table.version,
  };
}

/**
 * Marginal infrastructure cost of serving one query, rounded UP to whole micros
 * (sub-micro costs are real but the ledger is integer). Used for cache-margin tracking.
 */
export function servingCostPerQueryMicros(cacheStatus: "HIT" | "MISS" | string, table: InfraPriceTable): number {
  const hit = cacheStatus === "HIT" || cacheStatus === "STALE";
  let c = costExactMicros(table, "workers.requests", 1) + costExactMicros(table, "workers.cpu_ms", hit ? A.cpuMsHit : A.cpuMsMiss);
  c += costExactMicros(table, "analytics_engine.data_points", 1) + costExactMicros(table, "queues.operations", A.queueOpsPerMessage / A.usageEventsPerQueueMessage);
  if (!hit) {
    c += costExactMicros(table, "do.requests", A.missFanout);
    c += costExactMicros(table, "do.duration", A.missFanout * A.doSecondsPerRequest * A.doMemoryGb);
    c += costExactMicros(table, "do.sqlite.rows_read", A.rowsReadPerMiss);
  }
  return Math.max(1, Math.ceil(c));
}

export interface RevenueScenario { qpm: number; queriesPerMonth: number; grossMicros: number; feeMicros: number; providerGrossMicros: number; hostingMicros: number; netMicros: number }

/**
 * Revenue scenarios at several query rates. Fees are computed per query (exactly as the
 * ledger splits each charge) and then multiplied, so these figures reconcile with settlement.
 * If `inputs` + `table` are given, hosting is re-estimated at each QPM; otherwise `hosting` is used as-is.
 */
export function revenueScenarios(i: { pricePerQueryMicros: number; feeBps: number; hosting: CostEstimate; qpms?: number[]; inputs?: HostingInputs; table?: InfraPriceTable }): RevenueScenario[] {
  assertMicros(i.pricePerQueryMicros, "pricePerQueryMicros");
  const split = splitCharge(i.pricePerQueryMicros, i.feeBps);
  return (i.qpms ?? [10, 100, 1_000, 10_000]).map((qpm) => {
    const queriesPerMonth = Math.round(qpm * A.minutesPerMonth);
    const grossMicros = assertMicros(queriesPerMonth * i.pricePerQueryMicros);
    const feeMicros = assertMicros(queriesPerMonth * split.fee);
    const providerGrossMicros = grossMicros - feeMicros;
    const hostingMicros = i.inputs && i.table ? estimateMonthlyHostingCost({ ...i.inputs, queriesPerMinute: qpm }, i.table).expectedMicros : i.hosting.expectedMicros;
    return { qpm, queriesPerMonth, grossMicros, feeMicros, providerGrossMicros, hostingMicros, netMicros: providerGrossMicros - hostingMicros };
  });
}

/** Marketplace fee on an arbitrary amount (exposed for calculators). */
export const feeOn = (amountMicros: number, bps: number) => applyBps(amountMicros, bps);
